import { PDFArray, PDFDict, PDFName, PDFRawStream, decodePDFRawStream } from 'pdf-lib';

/**
 * Delete the ORIGINAL text of an edited paragraph from a page's content stream.
 *
 * "Edit text" used to paint a background-coloured box over the source words and
 * draw the replacement on top. On screen that reads as editing, but the file
 * still carries the old paragraph: it selects, copies, searches and screen-reads
 * underneath the patch, and the moment the cover is a point too narrow the tail
 * of every source line shows beside the new text. Real editors rewrite the page
 * instead, so this does the same — the show-text operators inside the replaced
 * rect are dropped, and nothing is left to peek out or to copy.
 *
 * Everything else in the stream is passed through byte for byte: kept operators
 * are re-emitted from the original bytes rather than re-serialised, so spacing,
 * inline images and any construct this walker does not model survive untouched.
 * Anything it cannot parse with confidence aborts the whole page — a cover that
 * looks slightly wrong beats a corrupted PDF.
 */

const WHITESPACE = new Set([0x00, 0x09, 0x0a, 0x0c, 0x0d, 0x20]);
const DELIMITER = new Set([0x28, 0x29, 0x3c, 0x3e, 0x5b, 0x5d, 0x7b, 0x7d, 0x2f, 0x25]);

export const isWhite = (c) => WHITESPACE.has(c);
export const isDelim = (c) => DELIMITER.has(c);
const isRegular = (c) => !isWhite(c) && !isDelim(c);

const IDENTITY = [1, 0, 0, 1, 0, 0];

/** PDF matrix product, row-vector convention: `m` applied, then `n`. */
function mul(m, n) {
    return [
        m[0] * n[0] + m[1] * n[2],
        m[0] * n[1] + m[1] * n[3],
        m[2] * n[0] + m[3] * n[2],
        m[2] * n[1] + m[3] * n[3],
        m[4] * n[0] + m[5] * n[2] + n[4],
        m[4] * n[1] + m[5] * n[3] + n[5],
    ];
}

/**
 * Split a content stream into tokens, keeping each one's byte span so kept
 * operators can be copied out of the source verbatim.
 *
 * @returns {Array<{ type: string, start: number, end: number, value?: number|string }> | null}
 */
export function tokenize(bytes) {
    const tokens = [];
    const n = bytes.length;
    let i = 0;

    while (i < n) {
        const c = bytes[i];

        if (isWhite(c)) { i += 1; continue; }

        // Comment
        if (c === 0x25) {
            while (i < n && bytes[i] !== 0x0a && bytes[i] !== 0x0d) i += 1;
            continue;
        }

        const start = i;

        // Literal string — parentheses nest, backslash escapes the next byte.
        if (c === 0x28) {
            let depth = 0;
            while (i < n) {
                const b = bytes[i];
                if (b === 0x5c) { i += 2; continue; }
                if (b === 0x28) depth += 1;
                else if (b === 0x29) {
                    depth -= 1;
                    if (depth === 0) { i += 1; break; }
                }
                i += 1;
            }
            if (depth !== 0) return null;
            tokens.push({ type: 'string', start, end: i });
            continue;
        }

        // Dictionary or hex string
        if (c === 0x3c) {
            if (bytes[i + 1] === 0x3c) {
                tokens.push({ type: 'dictOpen', start, end: i + 2 });
                i += 2;
                continue;
            }
            while (i < n && bytes[i] !== 0x3e) i += 1;
            if (i >= n) return null;
            i += 1;
            tokens.push({ type: 'string', start, end: i });
            continue;
        }
        if (c === 0x3e && bytes[i + 1] === 0x3e) {
            tokens.push({ type: 'dictClose', start, end: i + 2 });
            i += 2;
            continue;
        }

        if (c === 0x5b) { tokens.push({ type: 'arrayOpen', start, end: i + 1 }); i += 1; continue; }
        if (c === 0x5d) { tokens.push({ type: 'arrayClose', start, end: i + 1 }); i += 1; continue; }
        if (c === 0x7b || c === 0x7d) { tokens.push({ type: 'brace', start, end: i + 1 }); i += 1; continue; }

        // Name
        if (c === 0x2f) {
            i += 1;
            while (i < n && isRegular(bytes[i])) i += 1;
            tokens.push({ type: 'name', start, end: i });
            continue;
        }

        // Number or operator — both are runs of regular characters.
        while (i < n && isRegular(bytes[i])) i += 1;
        if (i === start) return null;
        let text = '';
        for (let k = start; k < i; k += 1) text += String.fromCharCode(bytes[k]);
        if (/^[+-]?(\d+\.?\d*|\.\d+)$/.test(text)) {
            tokens.push({ type: 'number', start, end: i, value: Number(text) });
        } else {
            tokens.push({ type: 'op', start, end: i, value: text });
        }
    }

    return tokens;
}

const SHOW_OPS = new Set(['Tj', 'TJ', "'", '"']);

/**
 * Rewrite one decoded content stream with the text inside `rects` removed.
 *
 * @param {Uint8Array} bytes decoded stream
 * @param {Array<{x0:number,y0:number,x1:number,y1:number}>} rects user space
 * @returns {{ bytes: Uint8Array, removed: number } | null} null = leave the page alone
 */
export function stripTextFromStream(bytes, rects) {
    const tokens = tokenize(bytes);
    if (!tokens) return null;

    const out = [];
    let groupStart = 0;
    let removed = 0;

    let ctm = IDENTITY;
    const ctmStack = [];
    let tm = IDENTITY;
    let tlm = IDENTITY;
    let leading = 0;
    const operands = [];

    const keepThrough = (end) => {
        if (end > groupStart) out.push(bytes.subarray(groupStart, end));
        groupStart = end;
    };
    const dropThrough = (end) => {
        groupStart = end;
    };
    const emit = (text) => {
        out.push(new TextEncoder().encode(text));
    };

    const inside = () => {
        const [, , , , e, f] = mul(tm, ctm);
        return rects.some((r) => e >= r.x0 - 1 && e <= r.x1 + 1 && f >= r.y0 - 1 && f <= r.y1 + 1);
    };

    for (let idx = 0; idx < tokens.length; idx += 1) {
        const token = tokens[idx];

        if (token.type === 'number') { operands.push(token.value); continue; }
        if (token.type !== 'op') { operands.push(null); continue; }

        const op = token.value;
        const nums = operands.filter((v) => typeof v === 'number');

        // Inline image: everything between `ID` and `EI` is raw sample data and
        // must never be tokenised. Copy it across untouched.
        if (op === 'ID') {
            let j = token.end + 1;
            for (;;) {
                if (j >= bytes.length - 1) return null;
                if (bytes[j] === 0x45 && bytes[j + 1] === 0x49
                    && (j === 0 || isWhite(bytes[j - 1]))
                    && (j + 2 >= bytes.length || isWhite(bytes[j + 2]) || isDelim(bytes[j + 2]))) {
                    j += 2;
                    break;
                }
                j += 1;
            }
            keepThrough(j);
            operands.length = 0;
            // Skip the tokens that fall inside the image data.
            while (idx + 1 < tokens.length && tokens[idx + 1].start < j) idx += 1;
            continue;
        }

        switch (op) {
            case 'q': ctmStack.push(ctm); break;
            case 'Q': ctm = ctmStack.pop() || IDENTITY; break;
            case 'cm': if (nums.length >= 6) ctm = mul(nums.slice(-6), ctm); break;
            case 'BT': tm = IDENTITY; tlm = IDENTITY; break;
            case 'Tm': if (nums.length >= 6) { tlm = nums.slice(-6); tm = tlm; } break;
            case 'TL': if (nums.length) leading = nums[nums.length - 1]; break;
            case 'Td':
                if (nums.length >= 2) {
                    tlm = mul([1, 0, 0, 1, nums[nums.length - 2], nums[nums.length - 1]], tlm);
                    tm = tlm;
                }
                break;
            case 'TD':
                if (nums.length >= 2) {
                    leading = -nums[nums.length - 1];
                    tlm = mul([1, 0, 0, 1, nums[nums.length - 2], nums[nums.length - 1]], tlm);
                    tm = tlm;
                }
                break;
            case 'T*':
                tlm = mul([1, 0, 0, 1, 0, -leading], tlm);
                tm = tlm;
                break;
            default: break;
        }

        if (SHOW_OPS.has(op)) {
            // `'` and `"` move to the next line before painting, so a dropped one
            // still has to advance — otherwise every later line of the paragraph
            // climbs onto the one above it.
            if (op === "'" || op === '"') {
                tlm = mul([1, 0, 0, 1, 0, -leading], tlm);
                tm = tlm;
            }
            if (inside()) {
                dropThrough(token.end);
                if (op === "'") emit(' T* ');
                if (op === '"') {
                    const aw = nums.length >= 2 ? nums[nums.length - 2] : 0;
                    const ac = nums.length >= 1 ? nums[nums.length - 1] : 0;
                    emit(` ${aw} Tw ${ac} Tc T* `);
                }
                removed += 1;
                operands.length = 0;
                continue;
            }
        }

        keepThrough(token.end);
        operands.length = 0;
    }

    if (!removed) return null;
    keepThrough(bytes.length);

    let total = 0;
    for (const chunk of out) total += chunk.length;
    const merged = new Uint8Array(total);
    let at = 0;
    for (const chunk of out) { merged.set(chunk, at); at += chunk.length; }
    return { bytes: merged, removed };
}

/** Every content stream of a pdf-lib page, as raw stream objects. */
export function contentStreamsOf(page) {
    const contents = page.node.get(PDFName.of('Contents'));
    const resolved = page.node.context.lookup(contents);
    if (resolved instanceof PDFRawStream) return [{ ref: contents, stream: resolved }];
    if (resolved instanceof PDFArray) {
        const list = [];
        for (let i = 0; i < resolved.size(); i += 1) {
            const ref = resolved.get(i);
            const stream = page.node.context.lookup(ref);
            if (!(stream instanceof PDFRawStream)) return null;
            list.push({ ref, stream });
        }
        return list;
    }
    return null;
}


/* ----------------------------------------------------------- images */

/**
 * Where every image on the page is actually DRAWN.
 *
 * An image XObject carries no position of its own: the page paints it by
 * setting a matrix and calling `Do`, and the same XObject can be painted twice,
 * at two sizes, on two pages. So "which picture did the user click" is a
 * question about the content stream, not about the resource dictionary — which
 * is why this walks the stream rather than reading `/XObject` and guessing.
 *
 * The matrix maps the unit square onto the page, so the four corners of that
 * square are the picture's corners.
 *
 * @returns {Array<{name:string, x0:number, y0:number, x1:number, y1:number}>}
 *   user space, in painting order. Empty when the page cannot be parsed.
 */
export function findImagePlacements(page) {
    let streams;
    try {
        streams = contentStreamsOf(page);
    } catch {
        return [];
    }
    if (!streams) return [];

    const images = imageNamesOf(page);
    if (!images.size) return [];

    const found = [];
    for (const { stream } of streams) {
        let bytes;
        try {
            bytes = decodePDFRawStream(stream).decode();
        } catch {
            continue;
        }
        const tokens = tokenize(bytes);
        if (!tokens) continue;

        let ctm = IDENTITY;
        const ctmStack = [];
        const operands = [];
        let lastName = null;

        for (let idx = 0; idx < tokens.length; idx += 1) {
            const token = tokens[idx];

            if (token.type === 'number') { operands.push(token.value); continue; }
            if (token.type === 'name') {
                let text = '';
                for (let k = token.start + 1; k < token.end; k += 1) text += String.fromCharCode(bytes[k]);
                lastName = text;
                continue;
            }
            if (token.type !== 'op') continue;

            const op = token.value;
            // Inline image data is raw samples, never tokens — skip past it.
            if (op === 'ID') {
                let j = token.end + 1;
                for (;;) {
                    if (j >= bytes.length - 1) { j = bytes.length; break; }
                    if (bytes[j] === 0x45 && bytes[j + 1] === 0x49 && isWhite(bytes[j - 1])) { j += 2; break; }
                    j += 1;
                }
                while (idx + 1 < tokens.length && tokens[idx + 1].start < j) idx += 1;
                operands.length = 0;
                continue;
            }

            const nums = operands.filter((v) => typeof v === 'number');
            if (op === 'q') ctmStack.push(ctm);
            else if (op === 'Q') ctm = ctmStack.pop() || IDENTITY;
            else if (op === 'cm' && nums.length >= 6) ctm = mul(nums.slice(-6), ctm);
            else if (op === 'Do' && lastName && images.has(lastName)) {
                const xs = [];
                const ys = [];
                for (const [u, v] of [[0, 0], [1, 0], [1, 1], [0, 1]]) {
                    xs.push(u * ctm[0] + v * ctm[2] + ctm[4]);
                    ys.push(u * ctm[1] + v * ctm[3] + ctm[5]);
                }
                found.push({
                    name: lastName,
                    x0: Math.min(...xs),
                    y0: Math.min(...ys),
                    x1: Math.max(...xs),
                    y1: Math.max(...ys),
                });
            }

            operands.length = 0;
            lastName = null;
        }
    }
    return found;
}

/** The names in this page's resources that are image XObjects. */
function imageNamesOf(page) {
    const names = new Set();
    const resources = page.node.Resources?.();
    if (!(resources instanceof PDFDict)) return names;
    const xobjects = resources.lookup(PDFName.of('XObject'), PDFDict);
    if (!xobjects) return names;
    for (const [key, value] of xobjects.entries()) {
        const stream = page.node.context.lookup(value);
        if (stream?.dict?.get(PDFName.of('Subtype')) === PDFName.of('Image')) {
            names.add(key.asString().slice(1));
        }
    }
    return names;
}

/**
 * Point one image name at a different image, for this page only.
 *
 * Resource dictionaries are shared: a logo drawn on every page is usually ONE
 * entry that every page's `/Resources` reaches through the same reference, so
 * setting it in place would replace the picture on all twenty-five of them.
 * Both dictionaries are therefore copied before the entry is touched, and the
 * copy is handed to this page alone.
 */
export function swapImageXObject(page, name, imageRef) {
    const context = page.node.context;
    const resources = page.node.Resources?.();
    if (!(resources instanceof PDFDict)) return false;

    const ownResources = resources.clone(context);
    const xobjects = ownResources.lookup(PDFName.of('XObject'), PDFDict);
    if (!xobjects) return false;
    const ownXobjects = xobjects.clone(context);

    ownXobjects.set(PDFName.of(name), imageRef);
    ownResources.set(PDFName.of('XObject'), ownXobjects);
    page.node.set(PDFName.of('Resources'), ownResources);
    return true;
}

/**
 * Remove the text painted inside `rects` (user space) from a page.
 *
 * @returns {number} operators removed; 0 means the page was left untouched
 */
export function stripTextInRects(page, rects) {
    if (!rects?.length) return 0;

    let streams;
    try {
        streams = contentStreamsOf(page);
    } catch {
        return 0;
    }
    if (!streams) return 0;

    const context = page.node.context;
    let removed = 0;

    for (const { ref, stream } of streams) {
        let decoded;
        try {
            decoded = decodePDFRawStream(stream).decode();
        } catch {
            return removed; // an unsupported filter: leave the rest of the page alone
        }

        let result;
        try {
            result = stripTextFromStream(decoded, rects);
        } catch {
            result = null;
        }
        if (!result) continue;

        try {
            const replacement = context.flateStream(result.bytes);
            context.assign(ref, replacement);
            removed += result.removed;
        } catch {
            return removed;
        }
    }

    return removed;
}
